import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';

/** Paging and ordering say nothing about what a step looked for. */
const QUIET_ARGS = new Set(['limit', 'offset', 'order', 'sort_by', 'cursor']);

/** Wrappers whose keys read on their own: `filters: {type: 'metric'}` is "type: metric". */
const FLATTENED_ARGS = new Set(['filters']);

type Arg = {name: string; value: string};

const words = (text: string): string => text.replace(/_/g, ' ').trim();

const plain = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return words(value) || undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value.map(plain).filter((part): part is string => part !== undefined);
    return parts.length > 0 ? parts.join(', ') : undefined;
  }
  return undefined;
};

/** A call's arguments as "group by: metric type" lines, one level of nesting flattened. */
const requestArgs = (args: Record<string, unknown>, skip: string[], prefix = ''): Arg[] =>
  Object.entries(args).flatMap(([key, value]): Arg[] => {
    if (QUIET_ARGS.has(key) || (!prefix && skip.includes(key)) || value == null) {
      return [];
    }
    const name = `${prefix}${words(key)}`;
    if (!prefix && typeof value === 'object' && !Array.isArray(value)) {
      const nested = FLATTENED_ARGS.has(key) ? ' ' : `${name} `;
      return requestArgs(value as Record<string, unknown>, skip, nested);
    }
    const shown = plain(value);
    return shown === undefined ? [] : [{name: name.trim(), value: shown}];
  });

/**
 * What a step was asked to do beyond what its label says: Dash's question, a
 * lookup's filters. `skip` lists the arguments the label already names.
 */
export const StepRequest = ({
  args,
  skip = [],
}: {
  args: Record<string, unknown>;
  skip?: string[];
}) => {
  const shown = requestArgs(args, skip);
  if (shown.length === 0) {
    return null;
  }
  return (
    <Stack gap="2">
      {shown.map(({name, value}) => (
        <Text key={name} size="xsmall" color="subtle" tagName="div">
          {name}: {value}
        </Text>
      ))}
    </Stack>
  );
};

/** Whether a call has arguments worth showing beyond `skip`. */
export const hasStepRequest = (
  args: Record<string, unknown> | undefined,
  skip: string[] = []
): boolean => args !== undefined && requestArgs(args, skip).length > 0;
